'use strict';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';


ImagePanelDirective.$inject = [
  '$timeout',
  'DeviceServices',
];

export function ImagePanelDirective(
  $timeout,
  DeviceServices,
) {
  return {
    restrict: 'E',
    replace: true,
    template: '<canvas>',
    scope: {
      imageUrl: '=',
      onTap: '&onImgTap',
      onDblTap: '&onImgDblTap',
      onLongPress: '&onImgLongPress',
      onSwipeUp: '&onImgSwipeUp',
      onSwipeDown: '&onImgSwipeDown',
      onSwipeLeft: '&onImgSwipeLeft',
      onSwipeRight: '&onImgSwipeRight'
    },
    link: function ($scope, $element, $attrs) {
      var canvas = $element[0];
      var subscriptions = [];
      var renderCanvas;

      $scope.$on('$destroy', destroy);

      init();

      function init() {
        canvas.width = $element.parent()[0].offsetWidth;
        canvas.height = $element.parent()[0].offsetHeight;

        $scope.$watch('imageUrl', function withNewUrl(newUrl) {
          destroy();

          if (newUrl) {

            renderCanvas = new ImgTouchCanvas({
              canvas: canvas,
              path: newUrl,
              desktop: !DeviceServices.isWebView()
            });

            subscriptions.push(renderCanvas.subscribe('tap', createTouchSubscriber('onTap')));
            subscriptions.push(renderCanvas.subscribe('dbltap', createTouchSubscriber('onDblTap')));
            subscriptions.push(renderCanvas.subscribe('longpress', createTouchSubscriber('onLongPress')));
            subscriptions.push(renderCanvas.subscribe('swipeup', createTouchSubscriber('onSwipeUp')));
            subscriptions.push(renderCanvas.subscribe('swipedown', createTouchSubscriber('onSwipeDown')));
            subscriptions.push(renderCanvas.subscribe('swipeleft', createTouchSubscriber('onSwipeLeft')));
            subscriptions.push(renderCanvas.subscribe('swiperight', createTouchSubscriber('onSwipeRight')));

            // temporary hack until a proper delegation system is put in place to access from controllers
            var reset = renderCanvas.returnToDefault.bind(renderCanvas);
            subscriptions.push(renderCanvas.swipeLeftSubject.subscribe(reset));
            subscriptions.push(renderCanvas.swipeRightSubject.subscribe(reset));
          }
        });
      }

      function createTouchSubscriber(event) {
        return {
          next: () => {
            //console.log('event fired: ' + event);
            try {
              const fn = $scope[event]();
              if (isFunction(fn)) {
                fn();
              }
            } catch (error) {
              console.warn('an error occurred attempting to call event ' + event);
              console.error(error);
            }
          },
          error: err => void 0,
          complete: () => void 0,
        };
      }

      function destroy() {
        let i = subscriptions.length;

        if (renderCanvas) {
          renderCanvas.dispose();
          renderCanvas = null;
        }

        while (i--) {
          const sub = subscriptions.pop();
          sub && sub.unsubscribe();
        }
      }
    }
  }
}

const SWIPE_SPEED_THRESHOLD = 1.5;
const TAP_DURATION_THRESHOLD = 300;
const DBLTAP_DURATION_THRESHOLD = 300;
const LONGPRESS_DURATION_THRESHOLD = 600;
const TAP_DISTANCE_THRESHOLD = 4;

const ZOOM_THRESHOLD = 0.0001;
const RESET_TIME = 350;

function ImgTouchCanvas(options) {
  if (!options || !options.canvas || !options.path) {
    throw 'ImgZoom constructor: missing arguments canvas or path';
  }

  //console.log('canvas client dims: ' + options.canvas.clientWidth + ' x ' + options.canvas.height);

  this.canvas = options.canvas;
  //this.canvas.width = this.canvas.width;
  //this.canvas.height = this.canvas.height;
  this.context = this.canvas.getContext('2d');
  this.subscriptions = [];

  //console.log('canvas dims: ' + this.canvas.width + ' x ' + this.canvas.height);

  this.desktop = options.desktop || false; //non touch events

  this.position = {
    x: 0,
    y: 0
  };
  this.scale = {
    x: 0.5,
    y: 0.5
  };
  this.imgTexture = new Image();
  this.imgTexture.src = options.path;

  this.frameRate = 60 / 1000;
  this.distanceTravelledX = null;
  this.distanceTravelledY = null;
  this.lastZoomScale = null;
  this.lastX = null;
  this.lastY = null;

  this.previousTouchEvent = {};

  this.mdown = false; //desktop drag

  this.init = false;

  this.imgTexture.onload = function () {
    requestAnimationFrame(this.animate.bind(this));
  }.bind(this);

  Object.defineProperties(this, {
    tapSubject: {value: new Subject()},
    dblTapSubject: {value: new Subject()},
    dblTapSubjectInternal: {value: new Subject()},
    longPressSubject: {value: new Subject()},
    swipeUpSubject: {value: new Subject()},
    swipeDownSubject: {value: new Subject()},
    swipeLeftSubject: {value: new Subject()},
    swipeRightSubject: {value: new Subject()},
    touchstartObservable: {value: Observable.fromEvent(this.canvas, 'touchstart')},
    touchmoveObservable: {value: Observable.fromEvent(this.canvas, 'touchmove')},
    touchendObservable: {value: Observable.fromEvent(this.canvas, 'touchend')}
  });

  this.subscriptions.push(this.tapSubject);
  this.subscriptions.push(this.dblTapSubject);
  this.subscriptions.push(this.dblTapSubjectInternal);
  this.subscriptions.push(this.longPressSubject);
  this.subscriptions.push(this.swipeUpSubject);
  this.subscriptions.push(this.swipeDownSubject);
  this.subscriptions.push(this.swipeLeftSubject);
  this.subscriptions.push(this.swipeRightSubject);

  this.setEventListeners();
}

ImgTouchCanvas.calculateEuclidianDistance = function (x1, y1, x2, y2) {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

ImgTouchCanvas.prototype = {
  animate: function () {
    var imgWidth;
    var imgHeight;

    //scale and center the image in the canvas by default.
    if (this.imgTexture.width) {
      if (this.init) {
        imgWidth = this.currentWidth();
        imgHeight = this.currentHeight();
      } else {
        imgWidth = this.imgTexture.width;
        imgHeight = this.imgTexture.height;
        var scale = Math.min(this.canvas.width / imgWidth, this.canvas.height / imgHeight, 1);
        this.scale.x = scale;
        this.scale.y = scale;

        imgWidth = scale * imgWidth;
        imgHeight = scale * imgHeight;

        this.position.x = Math.round((this.canvas.width - imgWidth) / 2);
        this.position.y = Math.round((this.canvas.height - imgHeight) / 2);

        this.init = true;
      }

      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

      this.context.drawImage(
        this.imgTexture,
        this.position.x, this.position.y,
        imgWidth, imgHeight);

      requestAnimationFrame(this.animate.bind(this));
    }
  },

  getRootY: function (imgHeight) {
    if (this.canvas.height > imgHeight) {
      return (this.canvas.height - imgHeight) / 2;
    } else {
      return 0;
    }
  },

  //TODO: add rotation
  gesturePinchZoom: function (event) {
    var zoom;

    if (event.targetTouches.length >= 2) {
      var p1 = event.targetTouches[0];
      var p2 = event.targetTouches[1];
      var zoomScale = ImgTouchCanvas.calculateEuclidianDistance(p1.pageX, p1.pageY, p2.pageX, p2.pageY);

      var diffX = p2.pageX - p1.pageX;
      var diffY = p2.pageY - p1.pageY;

      if (this.lastZoomScale) {
        zoom = zoomScale - this.lastZoomScale;
      }

      this.lastZoomScale = zoomScale;
    }

    this.doZoom(zoom, p1.pageX + diffX / 2, p1.pageY + diffY / 2);
  },

  doZoom: function (zoom, centerX, centerY) {
    if (!zoom) return;

    //new scale
    var currentScale = this.scale.x;
    var newScale = this.scale.x + zoom / 100;

    //some helpers
    var deltaScale = newScale - currentScale;
    var currentWidth = this.currentWidth();
    var currentHeight = this.currentHeight();
    var deltaWidth = this.imgTexture.width * deltaScale;
    var deltaHeight = this.imgTexture.height * deltaScale;
    var newWidth = currentWidth + deltaWidth;
    var newHeight = currentHeight + deltaHeight;

    centerX = centerX || (this.canvas.width) / 2;
    centerY = centerY || (this.canvas.height) / 2;

    // we must move the image to the left to keep the image centered
    // center defaults to the center of the canvas
    // center can be overridden with the current cursor position, or center of pinch gesture
    var offsetX = centerX - this.position.x;
    var offsetY = centerY - this.position.y;

    var scaleX = offsetX / currentWidth;
    var scaleY = offsetY / currentHeight;
    var newPosX = this.position.x - scaleX * deltaWidth;
    var newPosY = this.position.y - scaleY * deltaHeight;

    // do not allow the image to shrink smaller than the screen in both dimensions
    if (newWidth < Math.min(this.canvas.width, this.imgTexture.width) && newHeight < Math.min(this.canvas.height, this.imgTexture.height)) return;

    //finally update the values if the updated size is valid
    this.scale.x = newScale;
    this.scale.y = newScale;
    this.position.x = newPosX;
    this.position.y = newPosY;

    var finalCenterX = (newWidth + this.position.x) / 2;
    var finalCenterY = (newHeight + this.position.y) / 2;
    //console.log('center after zoom', finalCenterX, finalCenterY);
  },

  doMove: function (relativeX, relativeY) {
    //console.log([relativeX, relativeY]);
    if (this.lastX != null && this.lastY != null) {
      var deltaX = relativeX - this.lastX;
      var deltaY = relativeY - this.lastY;

      this.position.x += deltaX;
      this.position.y += deltaY;
    }

    this.lastX = relativeX;
    this.lastY = relativeY;
  },

  currentWidth: function () {
    return this.imgTexture.width * this.scale.x;
  },

  currentHeight: function () {
    return this.imgTexture.height * this.scale.y;
  },

  //TODO: animate with spring curve
  returnToCenter: function () {
    if (this.position.x > 0) {
      this.position.x = 0;
    } else if (this.position.x + this.currentWidth() < this.canvas.width) {
      this.position.x = this.canvas.width - this.currentWidth();
    }

    if (this.currentHeight() < this.canvas.height) {
      this.position.y = this.getRootY(this.scale.y * this.imgTexture.height);
    } else {
      if (this.position.y > 0) {
        this.position.y = 0;
      } else if (this.position.y + this.currentHeight() < this.canvas.height) {
        this.position.y = this.canvas.height - this.currentHeight();
      }
    }
  },

  returnToDefault: function (animate) {
    var imgWidth = this.imgTexture.width;
    var imgHeight = this.imgTexture.height;
    var scale = Math.min(this.canvas.width / imgWidth, this.canvas.height / imgHeight, 1);

    imgWidth = scale * imgWidth;
    imgHeight = scale * imgHeight;

    var finalX = Math.round((this.canvas.width - imgWidth) / 2);
    var finalY = Math.round((this.canvas.height - imgHeight) / 2);

    if (this.position.x === finalX && this.position.y === finalY && this.scale.x === scale) {
      this.publish('dbltap');
    } else {
      //TODO: animate zoom and movement
      this.startAnimation({
        duration: isUndefined(animate) || animate ? RESET_TIME : 1 / this.frameRate,
        finalX: finalX,
        finalY: finalY,
        finalScaleRatio: scale
      });
    }
  },

  setEventListeners: function () {
    var self = this;

    //TODO(annotations): filter the events to only pass when in view mode, not annotation mode
    this.subscriptions.push(this.touchstartObservable.subscribe(onTouchStart.bind(this)));
    this.subscriptions.push(this.touchmoveObservable.subscribe(onTouchMove.bind(this)));
    this.subscriptions.push(this.touchendObservable.subscribe(onTouchEnd.bind(this)));
    this.subscriptions.push(this.subscribe('dbltapInternal', self.returnToDefault.bind(self)));

    function onTouchStart(e) {
      this.startT = new Date().getTime();
      this.startX = e.targetTouches[0].pageX - this.canvas.getBoundingClientRect().left;
      this.startY = e.targetTouches[0].pageY - this.canvas.getBoundingClientRect().top;
      this.distanceTravelledX = 0;
      this.distanceTravelledY = 0;

      this.lastX = null;
      this.lastY = null;
      this.lastZoomScale = null;
    }

    function onTouchMove(e) {
      e.preventDefault();

      if (e.targetTouches.length == 2) { //pinch
        this.gesturePinchZoom(e);
      } else if (e.targetTouches.length == 1) {
        var relativeX = e.targetTouches[0].pageX - this.canvas.getBoundingClientRect().left;
        var relativeY = e.targetTouches[0].pageY - this.canvas.getBoundingClientRect().top;
        this.doMove(relativeX, relativeY);

        this.distanceTravelledX += Math.abs(relativeX);
        this.distanceTravelledY += Math.abs(relativeY);
      }
    }

    function onTouchEnd(e) {
      e.preventDefault();

      //console.log('touchend targetTouches.length=' + e.targetTouches.length + ', changedTouches.length=' + e.changedTouches.length);

      if (e.changedTouches.length == 1 && this.startX && this.startY) {
        var relativeT = new Date().getTime() || this.startTime;
        var relativeX = e.changedTouches[0].pageX - this.canvas.getBoundingClientRect().left;
        var relativeY = e.changedTouches[0].pageY - this.canvas.getBoundingClientRect().top;

        var deltaT = relativeT - this.startT;
        var deltaX = relativeX - this.startX;
        var deltaY = relativeY - this.startY;

        var currentSpeed = Math.max(Math.abs(deltaX) / deltaT, Math.abs(deltaY) / deltaT);

        //console.log('speed:' + currentSpeed + ', with deltas: [' + deltaX + ',' + deltaY + ',' + deltaT + ']');

        if (this.distanceTravelledX < TAP_DISTANCE_THRESHOLD && this.distanceTravelledY < TAP_DISTANCE_THRESHOLD) {
          if (this.previousTouchEvent.startT && ((relativeT - this.previousTouchEvent.startT) < DBLTAP_DURATION_THRESHOLD)) {
            this.publish('dbltapInternal');
          } else if (deltaT < TAP_DURATION_THRESHOLD) {
            this.publish('tap');
          } else if (deltaT > LONGPRESS_DURATION_THRESHOLD) {
            this.publish('longpress');
          }
        } else if (currentSpeed > SWIPE_SPEED_THRESHOLD) {
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX > 0) {
              this.publish('swiperight');
            } else {
              this.publish('swipeleft');
            }
          } else {
            if (deltaY > 0) {
              this.publish('swipedown');
            } else {
              this.publish('swipeup');
            }
          }
        }
      }

      if (e.targetTouches.length == 0) { //e.changedTouches.length == 1 &&
        this.returnToCenter();
      }

      this.previousTouchEvent = {
        startT: this.startT,
        endT: relativeT,

        startX: this.startX,
        endX: relativeX,

        startY: this.startY,
        endY: relativeY
      };
    }

    if (this.desktop) {

      this.onWheel = function (e) {
        if (e.deltaY < 0) {
          this.doZoom(5, e.pageX, e.pageY);
        } else if (e.deltaY > 0) {
          this.doZoom(-5, e.pageX, e.pageY);
        }
      }.bind(this);

      this.onMousedown = function (e) {
        e.targetTouches = [{pageX: e.pageX, pageY: e.pageY}];
        e.changedTouches = [{pageX: e.pageX, pageY: e.pageY}];
        onTouchStart.call(this, e);
        this.mdown = true;
      }.bind(this);

      this.onMouseup = function (e) {
        e.targetTouches = [{pageX: e.pageX, pageY: e.pageY}];
        e.changedTouches = [{pageX: e.pageX, pageY: e.pageY}];
        onTouchEnd.call(this, e);
        this.mdown = false;
      }.bind(this);

      this.onMousemove = function (e) {
        if (this.mdown) {
          e.targetTouches = [{pageX: e.pageX, pageY: e.pageY}];
          e.changedTouches = [{pageX: e.pageX, pageY: e.pageY}];
          onTouchMove.call(this, e);
        }
      }.bind(this);

      // keyboard+mouse
      this.canvas.addEventListener('wheel', this.onWheel);
      this.canvas.addEventListener('mousedown', this.onMousedown);
      this.canvas.addEventListener('mouseup', this.onMouseup);
      this.canvas.addEventListener('mousemove', this.onMousemove);
    }
  },

  publish: function (event) {
    //console.log('publish(' + event + ')');
    switch (event) {
      case 'tap':
        this.tapSubject.next();
        break;
      case 'dbltap':
        this.dblTapSubject.next();
        break;
      case 'dbltapInternal':
        this.dblTapSubjectInternal.next();
        break;
      case 'longpress':
        this.longPressSubject.next();
        break;
      case 'swipeup':
        this.swipeUpSubject.next();
        break;
      case 'swipedown':
        this.swipeDownSubject.next();
        break;
      case 'swipeleft':
        this.swipeLeftSubject.next();
        break;
      case 'swiperight':
        this.swipeRightSubject.next();
        break;
      default:
        throw new Error('ImgTouchCanvas.publish requires a valid event name. Received ' + event);
    }
  },

  subscribe: function (event, subscriber) {
    switch (event) {
      case 'tap':
        this.tapSubject.subscribe(subscriber);
        break;
      case 'dbltap':
        this.dblTapSubject.subscribe(subscriber);
        break;
      case 'dbltapInternal':
        this.dblTapSubjectInternal.subscribe(subscriber);
        break;
      case 'longpress':
        this.longPressSubject.subscribe(subscriber);
        break;
      case 'swipeup':
        this.swipeUpSubject.subscribe(subscriber);
        break;
      case 'swipedown':
        this.swipeDownSubject.subscribe(subscriber);
        break;
      case 'swipeleft':
        this.swipeLeftSubject.subscribe(subscriber);
        break;
      case 'swiperight':
        this.swipeRightSubject.subscribe(subscriber);
        break;
      default:
        throw new Error('ImgTouchCanvas.subscribeSwipe requires a valid event name. Received ' + event);
    }
  },

  //Animation helpers

  convertCenterToOrigin: function (centerX, centerY) {
    var self = this;
    return {
      x: centerX - self.currentWidth() / 2,
      y: centerY - self.currentHeight() / 2
    };
  },

  startAnimation: function (options) {
    //console.log('start animation:', options);
    var animationTickObservable = Observable
      .interval(1 / this.frameRate)
      .take(Math.ceil(options.duration * this.frameRate))
      .takeUntil(this.touchstartObservable); // currently only works properly on touch devices...

    var finalWidth = this.imgTexture.width * options.finalScaleRatio;
    var finalHeight = this.imgTexture.height * options.finalScaleRatio;

    var finalCenterX = (finalWidth + options.finalX) / 2;
    var finalCenterY = (finalHeight + options.finalY) / 2;
    var zoomDeltas = this.zoomDeltas(options.finalScaleRatio, options.finalX, options.finalY, finalCenterX, finalCenterY, options.duration);
    var sub = animationTickObservable.subscribe(function (interval) {
      //console.log('interval', zoomDeltas[interval]);
      //this.position.x = zoomDeltas[interval].posX;
      //this.position.y = zoomDeltas[interval].posY;
      zoomDeltas[interval] && this.doZoom(zoomDeltas[interval].zoom);
      //this.animate();
    }.bind(this));

    //TODO(HACK): fix this with real translation!!!
    var timeoutId = setTimeout(function () {
      this.scale.x = options.finalScaleRatio;
      this.scale.y = options.finalScaleRatio;
      this.position.x = options.finalX;
      this.position.y = options.finalY;
      sub && sub.unsubscribe();
      sub2 && sub2.unsubscribe();
    }.bind(this), options.duration + 15);

    var sub2 = this.touchstartObservable.subscribe(function () {
      clearTimeout(timeoutId);
      sub && sub.unsubscribe();
      sub2 && sub2.unsubscribe();
    })
  },

  //TODO: apply easing curve or similar
  //TODO: fix the math on this. it sort of works, but isn't quite right.
  zoomDeltas: function (finalScaleRatio, finalPosX, finalPosY, finalCenterX, finalCenterY, duration) {
    var currentScale = this.scale.x;
    var delta = finalScaleRatio - currentScale;
    if (currentScale === finalScaleRatio) {
      return [];
    } else if (Math.abs(delta) < ZOOM_THRESHOLD) {
      return [delta];
    } else {
      var result = [];
      var intervals = Math.ceil(duration * this.frameRate);
      var totalZoom = currentScale - 100 * finalScaleRatio;
      var zoomIncrement = totalZoom / intervals;

      // attempting to work out translation math
      //var currentX = this.position.x;
      //var currentY = this.position.y;
      //var currentWidth = this.currentWidth();
      //var currentHeight = this.currentHeight();
      //var currentCenterX = (currentWidth + this.position.x) / 2;
      //var currentCenterY = (currentHeight + this.position.y) / 2;
      //var deltaCenterX = finalCenterX - currentCenterX || 0;
      //var deltaCenterY = finalCenterY - currentCenterY || 0;
      //var incrementCXSize = deltaCenterX / intervals;
      //var incrementCYSize = deltaCenterY / intervals;
      //var imgWidth = this.imgTexture.width;
      //var imgHeight = this.imgTexture.height;
      //var updatedScale = currentScale + zoomIncrement * intervals / 100;
      //var deltaImgWidth = imgWidth * updatedScale - imgWidth;
      //var deltaImgHeight = imgHeight * updatedScale - imgHeight;

      for (var i = 0; i < intervals; i++) {
        //currentCenterX += incrementCXSize;
        //currentCenterY += incrementCYSize;
        result.push({zoom: zoomIncrement});//, posX: currentX, posY: currentY});
      }
      return result;
    }
  },

  dispose: function () {
    var i = this.subscriptions.length;

    while (i--) {
      var sub = this.subscriptions.pop();
      sub && sub.unsubscribe();
    }

    if (this.desktop) {
      this.canvas.removeEventListener('wheel', this.onWheel);
      this.canvas.removeEventListener('mousedown', this.onMousedown);
      this.canvas.removeEventListener('mouseup', this.onMouseup);
      this.canvas.removeEventListener('mousemove', this.onMousemove);
    }
  }
};



// WEBPACK FOOTER //
// ./src/js/core-components/file-viewer/ImagePanelDirective.js